import { flow } from 'fp-ts/function';

type ComparisonFunction<T> = (a: T, b: T) => ComparisonResult;
type ComparisonResult = -1 | 1 | 0;

interface StrategyMap<T> {
    [key: string]: ComparisonFunction<T>;
}

const isNumber = /^-?\d+$/;
export const castIntegerIfPossible = (a: string, b: string) =>
    isNumber.test(a) && isNumber.test(b) ? { aProp: parseInt(a, 10), bProp: parseInt(b, 10) } : { aProp: a, bProp: b };

export const reverseOrder =
    <T>(f: ComparisonFunction<T>) =>
    (a: T, b: T): ComparisonResult =>
        (f(a, b) * -1) as ComparisonResult;

export const defaultComparison = <T>(a: T, b: T) => {
    if (a < b) return -1;
    return b < a ? 1 : 0;
};
export const compareWithCastIntegerIfPossible = flow(castIntegerIfPossible, ({ aProp, bProp }) =>
    defaultComparison(aProp, bProp)
);
export const selectorComparator =
    <T, U>(fn: (arg: T) => U, comparison: ComparisonFunction<U> = defaultComparison) =>
    (a: T, b: T) => {
        const aValue = fn(a);
        const bValue = fn(b);
        return comparison(aValue, bValue);
    };
export const selectorPropComparator =
    <T, U>(fn: (arg: T) => U, comparison: ComparisonFunction<U | 0> = defaultComparison) =>
    (a: T, b: T) => {
        const aValue = fn(a) ?? 0;
        const bValue = fn(b) ?? 0;
        return comparison(aValue, bValue);
    };

export const truthyPropComparator =
    <T, U>(selector: (arg: T) => U | null | undefined) =>
    (a: T, b: T): ComparisonResult => {
        const aProp = selector(a) ?? null;
        const bProp = selector(b) ?? null;
        if (!aProp !== !bProp) {
            return aProp ? -1 : 1;
        }
        return 0;
    };

export const strategySwitchComparator =
    <T>(specifier: Function, strategyMap: StrategyMap<T>, def: ComparisonFunction<T> = () => 0) =>
    (a: T, b: T) => {
        const strategyA = specifier(a);
        const strategyB = specifier(b);
        if (strategyA && strategyA === strategyB) {
            const strategy = strategyMap[strategyA];
            if (strategy) return strategy(a, b);
        }
        return def(a, b);
    };

export const structuredComparator =
    <T>(rules: ComparisonFunction<T>[]) =>
    (a: T, b: T): ComparisonResult =>
        rules.reduce((acc: ComparisonResult, cur: ComparisonFunction<T>) => (acc !== 0 ? acc : cur(a, b)), 0);
